import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * WakingUpOverlay — ONBOARD-2 (Session 64 redesign)
 *
 * Silent rotating-logo spinner shown during Render cold-starts.
 * No text, no timer, no progress bar — just the logo rotating until
 * the server responds, then it disappears.
 *
 * Event contract (unchanged from original):
 *   'serverWaking' → show spinner   (fired by axios interceptor in api.js)
 *   'serverAwake'  → hide spinner   (fired by axios interceptor on any 200)
 *
 * Suppression:
 *   setSuppressWaking(true) in RaceDay.jsx prevents the spinner from
 *   appearing during active heats (wired in Session 63 — do not remove).
 *
 * Safety valve:
 *   Auto-hides after 130 seconds max to avoid being stuck forever — kept just
 *   above Login.jsx's own worst-case retry budget (15s + 110s = 125s) so the
 *   overlay never disappears out from under an in-flight login retry.
 *   Does NOT reload the page — that would interrupt timekeepers mid-race.
 *   The keep-alive ping (App.js) keeps the server warm so cold-starts
 *   should become rare in practice; a full Render deploy (rebuild + restart,
 *   not just wake-from-sleep) is the main case that still exceeds the old
 *   90s valve, so it's set to comfortably cover that instead.
 */

const LOGO_SIZE   = 80;  // px — logo diameter
const AUTO_HIDE_MS = 130_000; // 130s safety valve

const WakingUpOverlay = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let safetyTimer = null;

    const show = () => {
      setVisible(true);
      // Safety valve — auto-dismiss after 90s rather than page reload
      safetyTimer = setTimeout(() => setVisible(false), AUTO_HIDE_MS);
    };

    const hide = () => {
      setVisible(false);
      if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
    };

    window.addEventListener('serverWaking', show);
    window.addEventListener('serverAwake',  hide);
    return () => {
      window.removeEventListener('serverWaking', show);
      window.removeEventListener('serverAwake',  hide);
      if (safetyTimer) clearTimeout(safetyTimer);
    };
  }, []);

  if (!visible) return null;

  return (
    <Box sx={{
      position        : 'fixed',
      inset           : 0,
      zIndex          : 9998,
      display         : 'flex',
      alignItems      : 'center',
      justifyContent  : 'center',
      background      : 'rgba(1, 47, 63, 0.72)',
      backdropFilter  : 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
    }}>
      {/* Rotating logo container */}
      <Box sx={{
        width           : LOGO_SIZE,
        height          : LOGO_SIZE,
        borderRadius    : '20px',
        background      : 'linear-gradient(135deg, #006D77, #012F3F)',
        border          : '1.5px solid rgba(232,197,71,0.4)',
        display         : 'flex',
        alignItems      : 'center',
        justifyContent  : 'center',
        fontSize        : LOGO_SIZE * 0.5,
        animation       : 'lapisticsSpinLogo 1.2s linear infinite',
        boxShadow       : '0 0 24px rgba(0,109,119,0.45)',
        '@keyframes lapisticsSpinLogo': {
          from: { transform: 'rotate(0deg)' },
          to:   { transform: 'rotate(360deg)' },
        },
      }}>
        🏊
      </Box>
    </Box>
  );
};

export default WakingUpOverlay;
